import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import Ticker from "@/components/Ticker";
import MarketBar from "@/components/MarketBar";
import Header from "@/components/Header";
import BriefingSection from "@/components/BriefingSection";
import AnalysisSearchSection from "@/components/AnalysisSearchSection";
import CompareSection from "@/components/CompareSection";
import ScreenerSection from "@/components/ScreenerSection";
import CalendarSection from "@/components/CalendarSection";
import RobotAISection from "@/components/RobotAISection";
import NewsPopup from "@/components/NewsPopup";
import ScalpingSection from "@/components/ScalpingSection";
import WatchlistSection from "@/components/WatchlistSection";
import Calculators from "@/components/Calculators";
import ProbabilitySection from "@/components/ProbabilitySection";
import NewsSection from "@/components/NewsSection";
import TelegramSection from "@/components/TelegramSection";
import MembershipSection from "@/components/MembershipSection";
import TrialBanner from "@/components/TrialBanner";
import { Activity, ArrowRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import HeroChartBg from "@/components/HeroChartBg";
import { nextBriefingDate, nextBriefingLabel, isMarketClosedToday } from "@/lib/idxCalendar";

const two = (n) => String(n).padStart(2, "0");

function Hero() {
  const { user, login } = useAuth();
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const wib = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Jakarta" }));
  const next = nextBriefingDate(wib);
  const diff = Math.max(0, next - wib);
  const h = two(Math.floor(diff / 3600000));
  const m = two(Math.floor((diff % 3600000) / 60000));
  const s = two(Math.floor((diff % 60000) / 1000));
  const closedToday = isMarketClosedToday(wib);
  const whenLabel = nextBriefingLabel(wib, next);

  return (
    <div className="relative overflow-hidden grid-bg border-b border-[var(--border)]">
      <HeroChartBg />
      <div className="absolute -top-40 -right-32 w-[500px] h-[500px] rounded-full bg-[var(--accent)]/8 blur-[120px]" />
      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-16 lg:py-24 relative">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--surface-card)] border border-[var(--border)] text-xs font-medium text-[var(--text-secondary)] mb-6">
            <Sparkles size={13} className="text-accent" /> Analisis Bertenaga AI · Bursa Efek Indonesia (IHSG)
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Trading IDX Lebih <span className="text-accent">Terarah</span>,<br />Setiap Pagi Jam 08:30 WIB.
          </h1>
          <p className="text-[var(--text-secondary)] text-base sm:text-lg mt-5 max-w-2xl leading-relaxed">
            Briefing pasar otomatis, 2 rekomendasi saham inti, 3 saham scalping volatil, kalkulator manajemen risiko, probabilitas top gainer, dan berita saham real-time — dalam satu terminal.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <button onClick={() => document.getElementById("briefing")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-briefing-btn" className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[var(--accent)] text-[#07090E] font-semibold hover:bg-[var(--accent-hover)] transition-colors">
              Lihat Briefing Hari Ini <ArrowRight size={18} />
            </button>
            {!user && (
              <button onClick={login} data-testid="hero-login-btn" className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[var(--surface-card)] border border-[var(--border)] font-semibold hover:bg-[var(--surface-hover)] transition-colors">
                Masuk dengan Google
              </button>
            )}
          </div>
          {!user && (
            <p className="mt-4 text-sm text-accent font-semibold flex items-center gap-2" data-testid="trial-promo">
              <Sparkles size={15} /> Gratis 10 hari Full Akses Semua Fitur untuk pengguna baru — login dengan Email Google.
            </p>
          )}
          <div className="flex items-center gap-3 mt-10 p-4 rounded-xl bg-[var(--surface-card)]/70 border border-[var(--border)] w-fit">
            <div className="w-10 h-10 rounded-lg bg-[var(--accent)]/12 flex items-center justify-center">
              <Activity size={18} className="text-accent" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-[11px] text-[var(--text-muted)] uppercase tracking-wider">Update Briefing Berikutnya</p>
                {closedToday && (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--amber)]/15 text-amber" data-testid="market-closed-badge">Bursa Tutup</span>
                )}
              </div>
              <p className="font-mono-data font-bold text-xl" data-testid="hero-countdown">{h}:{m}:{s} <span className="text-sm text-[var(--text-muted)]">jam</span></p>
              <p className="text-[11px] text-[var(--text-secondary)] mt-0.5" data-testid="hero-countdown-when">Briefing {whenLabel}{closedToday ? " · bursa IDX libur hari ini" : ""}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <Ticker />
      <Header />
      <MarketBar />
      <Hero />
      <main className="max-w-[1400px] mx-auto px-4 lg:px-6 py-14 space-y-20">
        <TrialBanner />
        <AnalysisSearchSection />
        <CompareSection />
        <ScreenerSection />
        <RobotAISection />
        <BriefingSection />
        <ScalpingSection />
        <WatchlistSection />
        <Calculators />
        <ProbabilitySection />
        <CalendarSection />
        <NewsSection />
        <TelegramSection />
        <MembershipSection />
      </main>
      <footer className="border-t border-[var(--border)] py-10">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-6 text-center">
          <p className="font-heading font-extrabold text-lg">FAS<span className="text-accent">Trading</span></p>
          <p className="text-xs text-[var(--text-muted)] mt-3 max-w-2xl mx-auto leading-relaxed">
            Disclaimer: Seluruh konten di FAS Trading bersifat edukasi dan bukan ajakan jual/beli. Keputusan investasi sepenuhnya menjadi tanggung jawab masing-masing. Do Your Own Research (DYOR).
          </p>
          <p className="text-[11px] text-[var(--text-muted)] mt-4">© {new Date().getFullYear()} FAS Trading · Semua Bisa Jadi Trader Saham</p>
        </div>
      </footer>
      <NewsPopup />
    </div>
  );
}
